import { type AxiosInstance } from 'axios';
import { type Dictionary, isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type IEvaluationVariables } from '../../evaluate/variables/IEvaluationVariables.ts';
import { type IAssignableExpression } from '../assignable/IAssignableExpression.ts';
import { buildAsyncServiceParams } from './buildAsyncServiceParams.ts';
import { getEngineAsyncClient } from './client/getEngineAsyncClient.ts';
import { type IAsyncService } from './context/IAsyncService.ts';
import { fetchAsyncService } from './fetch/fetchAsyncService.ts';
import { parseAsyncServiceUrl } from './url/parseAsyncServiceUrl.ts';

export type IAsyncServiceInputConfig = Dictionary<IAssignableExpression>;

export enum AsyncServiceFetchResultStatus {
  SUCCESS = 'success',
  SKIPPED = 'skipped',
  ERROR = 'error',
}

/**
 * Ritorna la funzione che permette di chiamare il servizio asincrono
 * configurato, gestendone il risultato e lo stato di caricamento e errore.
 */
export function useAsyncServiceFetch(
  service: IAsyncService | null | undefined,
  axiosClient: AxiosInstance | undefined | null,
  /** Mappa tra i parametri di input della chiamata al servizio e i widget del form */
  inputs: IAsyncServiceInputConfig | null | undefined,
) {
  const [data, setData] = useState<any[] | null>(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFunction = useAsyncServiceFunction(service, axiosClient);

  const lastBody = useRef<Record<string, any> | null>(null);

  const fetchCount = useRef(0);

  // Controller della richiesta in volo: una nuova richiesta annulla la
  // precedente, così i risultati mostrati sono sempre quelli dell'ultima
  // richiesta avviata (non dell'ultima che termina)
  const abortControllerRef = useRef<AbortController | null>(null);

  // All'unmount annullo l'eventuale richiesta ancora in volo
  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const handleFetch = useMemo(() => {
    // Costruisco la funzione solo se tutti i componenti sono presenti
    if (!(service && inputs && axiosClient)) return null;

    if (!handleFunction) return null;

    return async (searchParam: any, variables: IEvaluationVariables) => {
      const params = buildAsyncServiceParams(service, inputs, variables);

      const body = { ...searchParam, ...params };

      // Se il body della chiamata è lo stesso della precedente, non faccio nulla
      if (isEqual(body, lastBody.current)) {
        return;
      }

      lastBody.current = body;

      // Annullo l'eventuale richiesta precedente e creo un
      // nuovo controller dedicato a questa richiesta. Il riferimento locale
      // `controller` (e non la ref, che nel frattempo può essere sostituita
      // da una richiesta successiva) viene usato dopo l'await per capire se
      // questa richiesta è stata annullata e va quindi ignorata.
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);

      // Raccolgo risultato ed eventuale errore senza rilanciarlo, per poter
      // prima verificare se la richiesta è stata annullata
      const result = await handleFunction(body, controller.signal).then(
        data => ({ data, error: null }),
        error => ({ data: null, error }),
      );

      // Richiesta obsoleta, annullata da una più recente (o dall'unmount):
      // non tocco lo stato, né il loading (la richiesta nuova è in volo).
      // Copre anche il caso di risposta già risolta prima dell'annullamento.
      if (controller.signal.aborted) return;

      // NB: è importante che sia aumentato solo dopo aver effettivamente
      // completato la chiamata, per evitare errori di sincronizzazione
      fetchCount.current += 1;

      setLoading(false);
      if (result.error) {
        // biome-ignore format: messaggio di log su riga singola per leggibilità
        console.warn(`Errore durante la chiamata al servizio ${service.name} (ID#${service.id})`, result.error);
        setError(result.error);
        return;
      }

      setData(result.data ?? []);
      setError(null);
    };
  }, [axiosClient, handleFunction, inputs, service]);

  return { data, loading, error, handleFetch, fetchCount };
}

/**
 * Ritorna la funzione da chiamare per eseguire il servizio asincrono
 * configurato.
 */
export function useAsyncServiceFunction(
  service: IAsyncService | null | undefined,
  axiosClient: AxiosInstance | undefined | null,
) {
  const handleFunction = useMemo(() => {
    if (!(service && axiosClient)) return null;

    return async (params: any, signal?: AbortSignal) => {
      const { url, originalParams } = parseAsyncServiceUrl(service);
      const httpClient = getEngineAsyncClient({
        isExternal: service.isExternal,
        apiClient: axiosClient,
      });

      return fetchAsyncService({
        service,
        url,
        originalParams,
        httpClient,
        params,
        signal,
      });
    };
  }, [axiosClient, service]);

  return handleFunction;
}
